/**
 * Scrubs secrets and PII from Sentry events, breadcrumbs, and structured logs
 * before they leave the device.
 *
 * Redacts any KEY ending in (case-insensitive, punctuation-insensitive):
 * authorization, cookie (covers set-cookie), token (covers accessToken /
 * refreshToken / resetToken / sessionToken, but NOT tokenType / tokenExpiry
 * — a look-alike key that merely DESCRIBES a token is not the secret
 * itself), password, pin (covers currentPin / newPin), pan, secret, apiKey,
 * cardNumber, cvv — inside request headers/data, breadcrumb data, `extra`,
 * `contexts`, span/trace attributes, and log attributes.
 *
 * Also redacts the VALUE of any name matching that same rule when it appears
 * as:
 *   - a query-string parameter, in a URL or a bare query string
 *     (`?token=abc123` -> `?token=[Redacted]`; `?access_token=`,
 *     `?api_key=`, `?payment_intent_client_secret=` likewise)
 *   - a JSON-encoded request/response body. Sentry SDKs frequently capture
 *     bodies as JSON STRINGS, not parsed objects (request.data, breadcrumb
 *     data.body, fetch/xhr request/response bodies) — a scrubber that only
 *     walks objects silently redacts nothing on those. Every string is
 *     tried as JSON first; on success the parsed value is recursively
 *     scrubbed and re-stringified. On failure (form-encoded, truncated, or
 *     plain text) a regex fallback redacts recognizable `"key":"value"` and
 *     `key=value` pairs for the same sensitive names.
 *
 * Breadcrumb data and log attributes reach these hooks RAW (the SDK only
 * normalizes an event's depth/breadth before beforeSend), so the walk is
 * bounded (MAX_SCRUB_DEPTH, MAX_JSON_PARSE_LENGTH), replaces a cycle with a
 * placeholder, and scrubs every path to a shared object — none of those ever
 * passes an unscrubbed original through, and no input makes a hook throw
 * (a throwing beforeSend drops the event; a throwing beforeBreadcrumb /
 * beforeSendLog throws into the app's own addBreadcrumb / logger call).
 *
 * Deliberately narrow in scope beyond that: does NOT touch `event.tags`
 * (Quality Triage's `service=...` bucketing tag must survive untouched),
 * `event.user`, `event.message`, or `event.exception` — see backend #1874 /
 * #1890 review.
 *
 * Pure, dependency-free functions — safe to unit test without booting the
 * Sentry SDK. This exact module is duplicated (not shared — separate repos)
 * across every Palate frontend that initializes Sentry.
 */

const REDACTED = "[Redacted]";
const CIRCULAR = "[Circular]";
const TRUNCATED = "[Truncated]";
const UNSERIALIZABLE = "[Unserializable]";

/**
 * Nesting levels walked before a subtree is replaced with TRUNCATED. Well
 * past Sentry's own normalizeDepth (3); matches the backend's
 * MAX_SCRUB_DEPTH. Without a cap a deep raw payload costs unbounded time and,
 * past the JS stack limit, throws a RangeError out of the hook.
 */
const MAX_SCRUB_DEPTH = 10;

/**
 * JSON-looking strings longer than this skip parse -> scrub -> re-stringify
 * (three full passes plus an object graph) and take the linear regex
 * fallback instead, which still redacts every recognizable pair.
 */
const MAX_JSON_PARSE_LENGTH = 100_000;

/**
 * Every name is matched as a case-/punctuation-insensitive SUFFIX of the
 * key, not a substring-anywhere match. Suffix (not substring) is what lets
 * "accessToken" / "refreshToken" / "resetToken" / "sessionToken" match
 * while "tokenType" / "tokenExpiry" (metadata ABOUT a token, not the secret
 * value) do not — those keys end in "type"/"expiry", not "token". Same
 * reasoning excludes "passwordPolicy", "apiKeyId", "secretQuestion", etc.
 * "cookie" also covers "set-cookie" (normalizes to "setcookie", which ends
 * in "cookie").
 * "cookies" covers the SDK's parsed `request.cookies` map — the plural key
 * does not end in "cookie", so without it every cookie value (incl. the
 * refresh token) shipped to Sentry verbatim (admin#936).
 */
const SENSITIVE_KEY_SUFFIXES = [
  "authorization",
  "cookie",
  "cookies",
  "token",
  "password",
  "pin",
  "pan",
  "secret",
  "apikey",
  "cardnumber",
  "cvv",
];

function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/** Exported for direct unit testing of the key-matching rules. */
export function isSensitiveKey(key: string): boolean {
  const normalized = normalizeKey(key);
  // "span" ends in "pan" but is never a card PAN. The SDK's own fetch/xhr
  // breadcrumbs carry `__span` (the id Sentry uses to link a breadcrumb to
  // its span), and redacting it breaks that link on every request.
  if (normalized.endsWith("span")) return false;
  return SENSITIVE_KEY_SUFFIXES.some((suffix) => normalized.endsWith(suffix));
}

/**
 * `?name=value` / `&name=value` / `#name=value` / a bare leading
 * `name=value` — value up to the next `&`/`#`/end. The name is judged by
 * isSensitiveKey (the same suffix rule as object keys), not an exact list.
 */
const QUERY_PARAM_PATTERN = /((?:^|[?&#])([^=&#?\s]+)=)[^&#]*/g;

/**
 * `"name": value` / `'name': value` inside a JSON-shaped fragment (valid or
 * truncated) — name judged by isSensitiveKey. Group 1 is everything up to
 * the value; groups 2/3 are the double-/single-quoted name.
 */
const JSON_PAIR_PATTERN =
  /((?:"((?:[^"\\]|\\.)*)"|'((?:[^'\\]|\\.)*)')\s*:\s*)(?:"(?:[^"\\]|\\.)*"?|'(?:[^'\\]|\\.)*'?|[^,}\]\s][^,}\]]*)/g;

/** Cheap pre-check before paying JSON.parse's cost/exceptions on strings that can never be JSON. */
function looksLikeJson(trimmed: string): boolean {
  return trimmed.length > 0 && (trimmed[0] === "{" || trimmed[0] === "[");
}

/** `%5F`-style encoded parameter names; a malformed escape is judged as-is. */
function decodeParamName(name: string): string {
  try {
    return decodeURIComponent(name);
  } catch {
    return name;
  }
}

/**
 * Regex fallback for a string that ISN'T parsed as JSON — form-encoded
 * bodies (`key=value&...`), URLs, and truncated/malformed/oversized JSON
 * fragments (`"key":"val`).
 */
function scrubFallback(value: string): string {
  return value
    .replace(
      JSON_PAIR_PATTERN,
      (pair: string, prefix: string, doubleQuotedKey?: string, singleQuotedKey?: string) =>
        isSensitiveKey(doubleQuotedKey ?? singleQuotedKey ?? "")
          ? `${prefix}${JSON.stringify(REDACTED)}`
          : pair,
    )
    .replace(QUERY_PARAM_PATTERN, (param: string, prefix: string, name: string) =>
      isSensitiveKey(decodeParamName(name)) ? `${prefix}${REDACTED}` : param,
    );
}

function scrubString(value: string, depth: number): string {
  const trimmed = value.trim();
  if (trimmed.length <= MAX_JSON_PARSE_LENGTH && looksLikeJson(trimmed)) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (parsed !== null && typeof parsed === "object") {
        return JSON.stringify(deepScrub(parsed, "", depth, newScrubState()));
      }
    } catch {
      // Truncated or malformed JSON — fall through to the regex fallback.
    }
  }
  return scrubFallback(value);
}

interface ScrubState {
  /** Objects already scrubbed -> their scrubbed copy, so a shared reference reuses it. */
  done: Map<object, unknown>;
  /** Objects on the current path — meeting one of these again is a cycle. */
  active: Set<object>;
}

function newScrubState(): ScrubState {
  return { done: new Map(), active: new Set() };
}

/**
 * Recursively redact sensitive keys/values into a fresh copy. Never returns
 * an original object: a second path to an already-scrubbed object gets its
 * scrubbed copy, a cycle gets CIRCULAR, anything past MAX_SCRUB_DEPTH gets
 * TRUNCATED, and an object that throws when read (revoked Proxy, throwing
 * getter) gets UNSERIALIZABLE.
 */
function deepScrub(value: unknown, key: string | number, depth: number, state: ScrubState): unknown {
  if (typeof key === "string" && isSensitiveKey(key)) {
    return REDACTED;
  }
  if (value == null) return value;
  if (typeof value === "string") return scrubString(value, depth);
  if (typeof value !== "object") return value;
  if (state.active.has(value)) return CIRCULAR;
  if (state.done.has(value)) return state.done.get(value);
  if (depth >= MAX_SCRUB_DEPTH) return TRUNCATED;
  state.active.add(value);
  try {
    let out: unknown;
    if (Array.isArray(value)) {
      out = value.map((item: unknown, i) => deepScrub(item, i, depth + 1, state));
    } else {
      const copy: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        copy[k] = deepScrub(v, k, depth + 1, state);
      }
      out = copy;
    }
    state.done.set(value, out);
    return out;
  } catch {
    return UNSERIALIZABLE;
  } finally {
    state.active.delete(value);
  }
}

/** Recursively scrub an arbitrary data object (request body, extra, contexts, log attributes). */
export function scrubData<T>(data: T): T {
  return deepScrub(data, "", 0, newScrubState()) as T;
}

interface ScrubbableBreadcrumb {
  message?: string;
  data?: unknown;
}

interface ScrubbableSpan {
  data?: unknown;
}

interface ScrubbableEvent {
  request?: unknown;
  extra?: unknown;
  contexts?: unknown;
  breadcrumbs?: ScrubbableBreadcrumb[];
  spans?: ScrubbableSpan[];
}

/**
 * beforeSend / beforeSendTransaction hook. Scrubs `request` (headers, data,
 * cookies, query_string, url), `extra`, `contexts`, every breadcrumb, and
 * every span's `data` (trace/span attributes, e.g. `http.url`). Never
 * touches `tags`, `user`, `message`, or `exception`.
 */
export function scrubEvent<E extends ScrubbableEvent>(event: E): E {
  if (!event) return event;
  const next = { ...event };
  if (next.request !== undefined) next.request = scrubData(next.request);
  if (next.extra !== undefined) next.extra = scrubData(next.extra);
  if (next.contexts !== undefined) next.contexts = scrubData(next.contexts);
  if (Array.isArray(next.breadcrumbs)) {
    next.breadcrumbs = next.breadcrumbs.map((b) => scrubBreadcrumb(b));
  }
  if (Array.isArray(next.spans)) {
    next.spans = next.spans.map((s) => scrubSpan(s));
  }
  return next;
}

/**
 * beforeBreadcrumb hook. Fetch/XHR breadcrumbs carry request/response
 * bodies (often as JSON strings) and full URLs in `data` — scrub both
 * `data` and `message`.
 */
export function scrubBreadcrumb<B extends ScrubbableBreadcrumb>(breadcrumb: B): B {
  if (!breadcrumb) return breadcrumb;
  const next = { ...breadcrumb };
  if (typeof next.message === "string") next.message = scrubString(next.message, 0);
  if (next.data !== undefined) next.data = scrubData(next.data);
  return next;
}

/** Scrubs a single span's `data` (e.g. `http.url`, request/response attributes). */
export function scrubSpan<S extends ScrubbableSpan>(span: S): S {
  if (!span) return span;
  const next = { ...span };
  if (next.data !== undefined) next.data = scrubData(next.data);
  return next;
}

/** beforeSendLog hook. Scrubs structured log attributes before they reach Sentry.logger. */
export function scrubLog<L extends { attributes?: Record<string, unknown> }>(log: L): L {
  if (!log) return log;
  if (!log.attributes) return log;
  return { ...log, attributes: scrubData(log.attributes) };
}
